/**
 * CMS 媒體庫（Phase 0）— 岸上所有素材的上傳與管理入口。
 *
 * 後續消費端：景點一/二階圖片語音影片、推薦點照片與 QR、窗屏全域影片
 * （assets/video/），最終由 asset-builder 打包成窗屏發布包。
 *
 * 上傳採 sha256 內容定址：重複上傳同一檔案不會產生第二份物件，UI 會明確標示
 * 「已存在」，避免使用者以為上傳失敗而反覆重試。
 *
 * **導覽以左側虛擬資料夾樹為主**。素材本身是扁平的（key 就是 sha256），樹是
 * 由路徑型標籤切出來的視圖——見 {@link MediaFolderTree}。這件事很重要：ITRI
 * 素材樹裡 `4_str4.txt` 這個檔名出現在 6 個不同資料夾，光看檔名分不出誰是誰，
 * 所以每一列都要把所屬路徑一起顯示出來。
 *
 * 本頁只負責狀態與流程；清單與網格兩種畫法在 {@link MediaTable} /
 * {@link MediaGrid}，兩者吃同一組 props（見 {@link MediaViewProps}）。
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../components/Button';
import { MediaBatchBar } from '../components/MediaBatchBar';
import {
  MediaBreadcrumb,
  MediaFolderTree,
  type MediaSelection,
} from '../components/MediaFolderTree';
import { MediaGrid } from '../components/MediaGrid';
import {
  MediaUsageModal,
  parseEditTags,
  type MediaEditState,
  type MediaViewProps,
} from '../components/MediaItemView';
import { MediaPreviewModal } from '../components/MediaPreview';
import { MediaTable } from '../components/MediaTable';
import { MediaUploadPanel } from '../components/MediaUploadPanel';
import {
  deleteMedia,
  listMedia,
  listMediaTags,
  updateMedia,
  usageOfError,
  type MediaItem,
  type MediaListItem,
  type MediaListFilter,
  type MediaType,
} from '../lib/api-client';

const MEDIA_TYPES: Array<{ value: MediaType | ''; label: string }> = [
  { value: '', label: '全部型別' },
  { value: 'image', label: '圖片' },
  { value: 'video', label: '影片' },
  { value: 'audio', label: '語音' },
  { value: 'document', label: '文件' },
];

/** 窗屏資產的語系代碼（zh/en/jp）——與 ITRI 檔案樹一致，非 CMS 的 zh-TW/ja。 */
const LANGUAGES: Array<{ value: string; label: string }> = [
  { value: '', label: '不分語系' },
  { value: 'zh', label: '中文 zh' },
  { value: 'en', label: '英文 en' },
  { value: 'jp', label: '日文 jp' },
];

const PAGE_SIZE = 50;

type SortKey = 'created_at' | 'filename';
type ViewMode = 'list' | 'grid';

export function MediaLibraryPage(): JSX.Element {
  const qc = useQueryClient();

  const [selection, setSelection] = useState<MediaSelection>({ kind: 'all' });
  const [mediaType, setMediaType] = useState<MediaType | ''>('');
  const [language, setLanguage] = useState('');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<SortKey>('created_at');
  const [view, setView] = useState<ViewMode>('list');
  const [offset, setOffset] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const [uploadLang, setUploadLang] = useState('');
  const [uploadTags, setUploadTags] = useState('');
  const [preview, setPreview] = useState<MediaItem | null>(null);
  const [usageOf, setUsageOf] = useState<MediaListItem | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [editing, setEditing] = useState<MediaEditState | null>(null);

  const filter: MediaListFilter = {
    limit: PAGE_SIZE,
    offset,
    sort,
    ...(mediaType ? { media_type: mediaType } : {}),
    ...(language ? { language } : {}),
    ...(q ? { q } : {}),
    ...(selection.kind === 'folder' ? { tag_prefix: selection.path } : {}),
    ...(selection.kind === 'label' ? { tag: selection.tag } : {}),
    ...(selection.kind === 'untagged' ? { untagged: true } : {}),
  };

  const query = useQuery({ queryKey: ['media.list', filter], queryFn: () => listMedia(filter) });
  const facetsQuery = useQuery({ queryKey: ['media.tags'], queryFn: listMediaTags });

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ['media.list'] });
    // 標籤統計也要重算，否則樹上的數字會停在改動前
    void qc.invalidateQueries({ queryKey: ['media.tags'] });
  };

  // 換資料夾 / 換篩選後留著上一頁的選取，會讓批次操作動到看不見的項目
  useEffect(() => {
    setSelectedIds(new Set());
  }, [selection, mediaType, language, q, offset]);

  const updateMutation = useMutation({
    mutationFn: (v: MediaEditState) =>
      updateMedia(v.id, { language: v.language, tags: parseEditTags(v.tags) }),
    onSuccess: () => {
      setEditing(null);
      invalidate();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (v: { id: string; force: boolean }) => deleteMedia(v.id, v.force),
    onSuccess: () => {
      setDeleteError(null);
      invalidate();
    },
    // 列表上的引用是載入當下的快照，期間可能有人剛掛上去。真正的守門在後端，
    // 所以 409 仍會發生——此時直接把後端回的引用位置攤開，不要只說「刪除失敗」。
    onError: (e: Error) => {
      const refs = usageOfError(e);
      if (!refs) {
        setDeleteError(`刪除失敗：${e.message}`);
        return;
      }
      const where = refs.map((r) => `${r.source_label}（${r.label}）`).join('、');
      setDeleteError(`刪除失敗：素材仍被 ${refs.length} 處使用 —— ${where}。請重新整理後再試。`);
    },
  });

  const changeSelection = (s: MediaSelection): void => {
    setSelection(s);
    setOffset(0);
    // 進資料夾預設按檔名排：同批上傳的時間戳幾乎相同，按時間排等於沒排。
    // 仍保留排序選單，讓這個切換是看得見的，不是暗地裡改行為。
    setSort(s.kind === 'folder' ? 'filename' : 'created_at');
  };

  const items = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const selectedItems = items.filter((m) => selectedIds.has(m.id));
  const allOnPageSelected = items.length > 0 && selectedItems.length === items.length;

  const toggle = (id: string): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /**
   * 刪除前的確認。
   *
   * 有引用時不是「再問一次要不要」，而是先把引用位置攤開來講——使用者需要的是
   * 「這張圖被藍色公路一號站的 poi_image 用著」，不是「確定嗎？」。真的要刪仍
   * 走得下去（force），但那是第二次、看過清單之後的決定。
   */
  const confirmDelete = (m: MediaListItem): void => {
    setDeleteError(null);
    const blocking = m.usage.refs.filter((r) => r.source !== 'release_manifest');
    if (blocking.length === 0) {
      if (window.confirm(`確定刪除「${m.filename}」？`)) {
        deleteMutation.mutate({ id: m.id, force: false });
      }
      return;
    }
    const where = blocking
      .slice(0, 5)
      .map((r) => `· ${r.source_label}：${r.label}`)
      .join('\n');
    const more = blocking.length > 5 ? `\n…等共 ${blocking.length} 處` : '';
    const ok = window.confirm(
      `「${m.filename}」仍被下列位置使用：\n\n${where}${more}\n\n` +
        '刪除後這些位置會抓不到檔案。仍要強制刪除嗎？',
    );
    if (ok) deleteMutation.mutate({ id: m.id, force: true });
  };

  const parsedUploadTags = parseEditTags(uploadTags);

  const viewProps: MediaViewProps = {
    items,
    languages: LANGUAGES,
    selectedIds,
    onToggle: toggle,
    onToggleAll: () =>
      setSelectedIds(allOnPageSelected ? new Set() : new Set(items.map((m) => m.id))),
    allSelected: allOnPageSelected,
    editing,
    onEditChange: setEditing,
    onSave: (e) => updateMutation.mutate(e),
    saving: updateMutation.isPending,
    onPreview: setPreview,
    onDelete: confirmDelete,
    onShowUsage: setUsageOf,
    onFolder: changeSelection,
  };

  return (
    <div>
      <div className="page-head">
        <h1 className="page-head__title">媒體庫</h1>
        <div className="page-head__actions">
          <Button
            variant="secondary"
            onClick={() => {
              void query.refetch();
              void facetsQuery.refetch();
            }}
            disabled={query.isFetching}
          >
            {query.isFetching ? '更新中…' : '重新整理'}
          </Button>
        </div>
      </div>

      <p className="page-intro">
        景點圖片 / 語音 / 影片、推薦點照片與窗屏全域影片的共用素材庫。上傳採 sha256
        內容定址，同一檔案重複上傳只會保留一份；左側資料夾由上傳時的路徑標籤自動產生。
        標示「使用中」的素材已被景點或商品引用，刪除前需先解除掛載。
      </p>

      {/*
        上傳區與清單同屬右欄。
        原本上傳區在資料夾樹**上方**、佔整頁寬，清單卻在樹右邊被縮排 240 px，
        於是上傳完成後留在畫面上的佇列表格會比下方的素材表格寬出一整個側欄
        （實測 1597 vs 1341 px，左緣也差 256 px），兩張卡片對不齊。
      */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', marginTop: 12 }}>
        <MediaFolderTree
          facets={facetsQuery.data}
          selection={selection}
          onSelect={changeSelection}
        />

        {/* minWidth: 0 —— 沒有它 flex 子項不會收縮，寬表格會把整頁撐出橫向捲動 */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* 批次設定：套用到本次加入佇列的每一個檔案 */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label style={{ fontSize: 13 }}>
              語系
              <br />
              <select
                value={uploadLang}
                onChange={(e) => setUploadLang(e.target.value)}
                style={{ padding: 6, minWidth: 130 }}
              >
                {LANGUAGES.map((l) => (
                  <option key={l.value} value={l.value}>
                    {l.label}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ fontSize: 13, flex: '1 1 260px' }}>
              標籤（逗號分隔）
              <br />
              <input
                type="text"
                value={uploadTags}
                onChange={(e) => setUploadTags(e.target.value)}
                placeholder="例：display, global-video, landing"
                style={{ width: '100%', padding: 6 }}
              />
            </label>
          </div>

          <MediaUploadPanel
            language={uploadLang}
            tags={parsedUploadTags}
            onUploaded={invalidate}
            onPreview={setPreview}
          />

          <MediaBreadcrumb selection={selection} onSelect={changeSelection} />

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '10px 0' }}>
            <select
              value={mediaType}
              onChange={(e) => {
                setMediaType(e.target.value as MediaType | '');
                setOffset(0);
              }}
              style={{ padding: 6 }}
            >
              {MEDIA_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
            <select
              value={language}
              onChange={(e) => {
                setLanguage(e.target.value);
                setOffset(0);
              }}
              style={{ padding: 6 }}
            >
              {LANGUAGES.map((l) => (
                <option key={l.value} value={l.value}>
                  {l.label}
                </option>
              ))}
            </select>
            <input
              type="text"
              placeholder="檔名搜尋"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setOffset(0);
              }}
              style={{ padding: 6, width: 180 }}
            />
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as SortKey)}
              style={{ padding: 6 }}
            >
              <option value="created_at">最近上傳</option>
              <option value="filename">檔名</option>
            </select>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
              <Button
                variant={view === 'list' ? 'primary' : 'secondary'}
                onClick={() => setView('list')}
              >
                ☰ 清單
              </Button>
              <Button
                variant={view === 'grid' ? 'primary' : 'secondary'}
                onClick={() => setView('grid')}
              >
                ▦ 網格
              </Button>
            </div>
          </div>

          {selectedItems.length > 0 && (
            <MediaBatchBar
              selected={selectedItems}
              languages={LANGUAGES}
              onDone={invalidate}
              onClear={() => setSelectedIds(new Set())}
            />
          )}

          {deleteError && <div className="error-banner">{deleteError}</div>}
          {query.isLoading && <div>載入中…</div>}
          {query.error && <div className="error-banner">{(query.error as Error).message}</div>}

          {query.data && (
            <>
              {view === 'list' ? <MediaTable {...viewProps} /> : <MediaGrid {...viewProps} />}

              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'center',
                  margin: '12px 0',
                  fontSize: 13,
                }}
              >
                <Button
                  variant="secondary"
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                  disabled={offset === 0}
                >
                  ← 上一頁
                </Button>
                <span>
                  {total === 0 ? 0 : offset + 1}–{Math.min(offset + PAGE_SIZE, total)} / 共 {total}{' '}
                  筆
                </span>
                <Button
                  variant="secondary"
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                  disabled={offset + PAGE_SIZE >= total}
                >
                  下一頁 →
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      {preview && <MediaPreviewModal item={preview} onClose={() => setPreview(null)} />}
      {usageOf && <MediaUsageModal item={usageOf} onClose={() => setUsageOf(null)} />}
    </div>
  );
}
